import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money, fecha, iniciales } from "@/lib/format";
import { Loading } from "@/components/Common";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const Dato = ({ k, v }) => (
  <div className="flex justify-between gap-4 border-b border-border/50 py-2 text-sm">
    <span className="text-muted-foreground">{k}</span>
    <span className="text-right font-medium">{v || "—"}</span>
  </div>
);

export const FichaEstudiante = ({ estudianteId, onClose }) => {
  const [f, setF] = useState(null);

  useEffect(() => {
    if (!estudianteId) return;
    setF(null);
    api.get(`/estudiantes/${estudianteId}/ficha`).then((r) => setF(r.data));
  }, [estudianteId]);

  return (
    <Dialog open={!!estudianteId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="modal-ficha-estudiante">
        <DialogHeader>
          <DialogTitle className="font-display">
            {f ? `Ficha de ${f.estudiante.nombre}` : "Ficha del estudiante"}
          </DialogTitle>
          <DialogDescription>
            {f ? `${f.estudiante.grado} ${f.estudiante.seccion || ""} · Matrícula ${f.estudiante.matricula}`
              : "Cargando información académica y financiera."}
          </DialogDescription>
        </DialogHeader>
        {!f ? <Loading label="Cargando ficha…" /> : (
          <>
            <div className="flex items-center gap-4">
              <div className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-primary/10 font-display text-xl font-bold text-primary">
                {iniciales(f.estudiante.nombre)}
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant={f.estudiante.estado === "activo" ? "secondary" : "outline"}>{f.estudiante.estado}</Badge>
                <Badge variant="outline">Promedio {f.promedio || "—"}</Badge>
                <Badge variant="outline">Asistencia {f.asistencia_pct}%</Badge>
                <Badge variant={f.balance_pendiente > 0 ? "destructive" : "secondary"}>
                  Balance {money(f.balance_pendiente)}
                </Badge>
                <Badge variant={f.contrato?.firmado ? "secondary" : "outline"}>
                  {f.contrato?.firmado ? "Contrato firmado" : "Contrato sin firma"}
                </Badge>
                {f.contrato?.pdf_nombre && <Badge>Contrato PDF</Badge>}
              </div>
            </div>

            <Tabs defaultValue="general" className="mt-4">
              <TabsList>
                <TabsTrigger value="general" data-testid="ficha-tab-general">General</TabsTrigger>
                <TabsTrigger value="academico" data-testid="ficha-tab-academico">Académico</TabsTrigger>
                <TabsTrigger value="financiero" data-testid="ficha-tab-financiero">Financiero</TabsTrigger>
                <TabsTrigger value="extras" data-testid="ficha-tab-extras">Combos y docs</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="mt-5 grid gap-6 sm:grid-cols-2">
                <div>
                  <h4 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                    Datos del estudiante
                  </h4>
                  <div className="mt-3">
                    <Dato k="Nacimiento" v={f.estudiante.fecha_nacimiento} />
                    <Dato k="Grado" v={`${f.estudiante.grado} ${f.estudiante.seccion || ""}`} />
                    <Dato k="Datos médicos" v={f.estudiante.datos_medicos} />
                    <Dato k="Alergias" v={f.estudiante.alergias} />
                    <Dato k="Diagnóstico" v={f.estudiante.diagnostico} />
                    <Dato k="Emergencia" v={f.estudiante.contacto_emergencia} />
                  </div>
                </div>
                <div>
                  <h4 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                    Padre / tutor
                  </h4>
                  <div className="mt-3">
                    <Dato k="Nombre" v={f.tutor?.nombre} />
                    <Dato k="Correo" v={f.tutor?.email} />
                    <Dato k="Teléfono" v={f.tutor?.telefono} />
                    <Dato k="Dirección" v={f.tutor?.direccion} />
                  </div>
                  <h4 className="mt-6 font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                    Modalidad de pago
                  </h4>
                  <div className="mt-3">
                    <Dato k="Modalidad" v={f.plan_pago?.nombre} />
                    <Dato k="Monto" v={f.plan_pago ? money(f.plan_pago.monto_base * (1 - (f.plan_pago.descuento_pct || 0) / 100)) : null} />
                    <Dato k="Cuotas" v={f.plan_pago?.cuotas} />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="academico" className="mt-5">
                <h4 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  Materias asignadas ({f.materias.length})
                </h4>
                <div className="mt-3 flex flex-wrap gap-2">
                  {f.materias.map((m) => <Badge key={m.id} variant="outline">{m.nombre}</Badge>)}
                  {f.materias.length === 0 && <p className="text-sm text-muted-foreground">Sin materias asignadas.</p>}
                </div>
                <h4 className="mt-6 font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  Calificaciones
                </h4>
                <table className="mt-3 w-full text-sm" data-testid="ficha-tabla-notas">
                  <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Materia</th>
                      <th className="px-3 py-2">Periodo</th>
                      <th className="px-3 py-2">Nota</th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.calificaciones.map((c) => (
                      <tr key={c.id} className="border-t border-border/60">
                        <td className="px-3 py-2">{c.materia_nombre}</td>
                        <td className="px-3 py-2 text-muted-foreground">{c.periodo}</td>
                        <td className={`px-3 py-2 font-mono font-semibold ${c.nota < 70 ? "text-destructive" : "text-primary"}`}>
                          {c.nota}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {f.calificaciones.length === 0 && (
                  <p className="mt-3 text-sm text-muted-foreground">Aún no hay calificaciones publicadas.</p>
                )}
              </TabsContent>

              <TabsContent value="financiero" className="mt-5">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="rounded-md border border-border/60 p-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Balance</p>
                    <p className="mt-1 font-display text-xl font-bold">{money(f.balance_pendiente)}</p>
                  </div>
                  <div className="rounded-md border border-border/60 p-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Pagado</p>
                    <p className="mt-1 font-display text-xl font-bold text-primary">{money(f.total_pagado)}</p>
                  </div>
                  <div className="rounded-md border border-border/60 p-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Facturas</p>
                    <p className="mt-1 font-display text-xl font-bold">{f.facturas.length}</p>
                  </div>
                </div>
                <table className="mt-5 w-full text-sm" data-testid="ficha-tabla-facturas">
                  <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">NCF</th>
                      <th className="px-3 py-2">Concepto</th>
                      <th className="px-3 py-2">Vence</th>
                      <th className="px-3 py-2">Total</th>
                      <th className="px-3 py-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.facturas.map((x) => (
                      <tr key={x.id} className="border-t border-border/60">
                        <td className="px-3 py-2 font-mono text-xs">{x.ncf}</td>
                        <td className="px-3 py-2">{x.detalle?.[0]?.concepto}</td>
                        <td className="px-3 py-2 font-mono text-xs">{fecha(x.fecha_vencimiento)}</td>
                        <td className="px-3 py-2 font-mono font-semibold">{money(x.monto_total)}</td>
                        <td className="px-3 py-2">
                          <Badge variant={x.estado === "pagado" ? "secondary" : x.estado === "en_verificacion" ? "default" : "outline"}>
                            {x.estado === "en_verificacion" ? "en verificación" : x.estado}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TabsContent>

              <TabsContent value="extras" className="mt-5 grid gap-6 sm:grid-cols-2">
                <div>
                  <h4 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                    Combos
                  </h4>
                  <div className="mt-3 space-y-2">
                    {f.combos.map((c) => (
                      <p key={c.id} className="text-sm">• {c.nombre} <span className="text-muted-foreground">{money(c.precio_total)}</span></p>
                    ))}
                    {f.combos.length === 0 && <p className="text-sm text-muted-foreground">Sin combos.</p>}
                  </div>
                  <h4 className="mt-6 font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                    Actividades
                  </h4>
                  <div className="mt-3 space-y-2">
                    {f.actividades.map((a) => (
                      <p key={a.id} className="text-sm">• {a.nombre} <span className="text-muted-foreground">{money(a.costo)}</span></p>
                    ))}
                    {f.actividades.length === 0 && <p className="text-sm text-muted-foreground">Sin actividades.</p>}
                  </div>
                </div>
                <div>
                  <h4 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                    Documentos del expediente
                  </h4>
                  <div className="mt-3 space-y-2">
                    {f.documentos.map((d) => (
                      <p key={d.id} className="text-sm">• {d.nombre} <span className="text-muted-foreground">({d.tipo})</span></p>
                    ))}
                    {f.documentos.length === 0 && <p className="text-sm text-muted-foreground">Sin documentos.</p>}
                  </div>
                  <h4 className="mt-6 font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
                    Contrato
                  </h4>
                  <div className="mt-3">
                    <Dato k="Año lectivo" v={f.contrato?.anio_lectivo} />
                    <Dato k="Firmado por" v={f.contrato?.firmado_por} />
                    <Dato k="Archivo PDF" v={f.contrato?.pdf_nombre} />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
